<template>
    <div class="data-page">
        <PageHeader title="Clientes" :subtitle="summaryText">
            <template #actions>
                <Button label="Cadastrar Cliente" icon="pi pi-plus" @click="openCreateModal" />
            </template>
        </PageHeader>

        <div class="data-card">
            <div class="data-card-toolbar">
                <span class="p-input-icon-left data-search">
                    <i class="pi pi-search" />
                    <InputText type="text" v-model="state.search" placeholder="Buscar por cliente, fornecedor ou URL..." />
                </span>
            </div>

            <DataTable class="data-table" paginator :value="filteredClients" :rowsPerPageOptions="[20, 50, 100]" lazy
                :loading="state.loading.clients || stateVendors.loading.vendors" :rows="state.perPage" ref="dt" dataKey="id"
                :totalRecords="state.totalRecords" :autoLayout="true" responsiveLayout="scroll"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                @page="onPage($event)">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ state.search ? 'Nenhum cliente encontrado para essa busca.' : 'Nenhum cliente cadastrado.' }}</span>
                    </div>
                </template>

                <Column field="id" header="ID" class="col-id">
                    <template #body="slotProps">
                        <span class="record-id">{{ formatId(slotProps.data.id) }}</span>
                    </template>
                </Column>
                <Column field="name" header="Cliente">
                    <template #body="slotProps">
                        <span class="record-name">{{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column field="vendors" header="Fornecedores">
                    <template #body="slotProps">
                        <div class="pill-list">
                            <span v-for="(vendor, index) in slotProps.data.vendors" :key="`${vendor.vendorId}-${index}`"
                                class="pill pill-info"
                                :title="vendor.vendorIsActive ? vendor.vendorName : `${vendor.vendorName} (fornecedor inativo)`">
                                {{ vendor.vendorName }}
                            </span>
                            <span v-if="!slotProps.data.vendors || !slotProps.data.vendors.length" class="empty-value">—</span>
                        </div>
                    </template>
                </Column>
                <Column field="callbackUrl" header="Callback URL">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.callbackUrl" class="mono-chip" :title="slotProps.data.callbackUrl">{{ slotProps.data.callbackUrl }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="fallbackEnabled" header="Fallback">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="slotProps.data.fallbackEnabled ? 'is-on' : 'is-off'">
                            <span class="pill-dot"></span>
                            {{ changeStatusBoleanToText(slotProps.data.fallbackEnabled) }}
                        </span>
                    </template>
                </Column>
                <Column class="col-actions">
                    <template #header>
                        Ações
                    </template>
                    <template #body="slotProps">
                        <div class="flex justify-content-end">
                            <SplitButton class="table-action-button" label="Ver Mais" icon="pi pi-eye"
                                :model="actionMenuItems(slotProps.data)" @click="openDetailsModal(slotProps.data)" />
                        </div>
                    </template>
                </Column>

                <template #paginatorstart>
                    <TableResults :shown="filteredClients.length" :total="state.totalRecords"
                        :pageTotal="state.clients.length" :filtering="!!state.search" />
                </template>
            </DataTable>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.createModal" :style="{ width: '30rem' }" modal
            header="Cadastrar cliente" @update:visible="handleCreateModalClose">

            <form class="data-form-grid data-form-single" @submit.prevent="tryToRegisterClient">
                <div class="form-field">
                    <label for="new-client-name">Nome</label>
                    <InputText id="new-client-name" type="text" v-model="state.newClient.name" autofocus />
                </div>

                <div class="form-field">
                    <label for="new-client-callback">Callback URL</label>
                    <InputText id="new-client-callback" type="text" v-model="state.newClient.callbackUrl"
                        placeholder="https://..." />
                </div>

                <div class="form-field">
                    <label for="new-client-attempts">Tentativas de notificação</label>
                    <InputNumber inputId="new-client-attempts" v-model="state.newClient.webhookMaxAttempts" :min="1"
                        :max="10" showButtons />
                    <small class="data-detail-hint">
                        Envios automáticos ao callback antes de liberar o reenvio manual no painel.
                    </small>
                </div>

                <button type="submit" class="hidden"></button>
            </form>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="state.createModal = false" />
                <Button :loading="state.loading.create" type="button" label="Registrar cliente" icon="pi pi-check"
                    @click="tryToRegisterClient" />
            </template>

        </Dialog>

        <Dialog class="data-dialog" v-model:visible="state.newClientTokenModal" :style="{ width: '34rem' }" modal
            header="Token do cliente" @update:visible="handleNewClientTokenClose">

            <div class="data-form-grid data-form-single">
                <Message severity="warn" sticky :closable="false">
                    Token de visualização única: guarde-o em um lugar seguro, não será possível vê-lo novamente.
                </Message>

                <div class="token-box">
                    <code>{{ state.newClientToken }}</code>
                    <Button class="p-button-text p-button-sm" type="button" icon="pi pi-copy"
                        v-tooltip.left="{ value: 'Copiar token' }" @click="copyToken(state.newClientToken)" />
                </div>
            </div>

            <template #footer>
                <Button type="button" label="Já guardei o token" icon="pi pi-check"
                    @click="state.newClientTokenModal = false" />
            </template>

        </Dialog>

        <Dialog class="data-dialog" v-model:visible="state.detailsModal" :style="{ width: '34rem' }" modal
            header="Editar cliente">

            <form class="data-form-grid data-form-single" @submit.prevent="saveChanges">

                <div class="data-dialog-subject">
                    <span class="pill pill-muted">ID {{ formatId(state.client.id) }}</span>
                    <span>{{ state.client.name }}</span>
                </div>

                <div class="form-field">
                    <label for="client-name">Nome</label>
                    <InputText id="client-name" type="text" v-model="state.modifiedClient.name" />
                </div>

                <div class="form-field">
                    <label for="client-callback">Callback URL</label>
                    <InputText id="client-callback" type="text" v-model="state.modifiedClient.callbackUrl"
                        placeholder="https://..." />
                </div>

                <div class="form-field">
                    <label for="client-attempts">Tentativas de notificação</label>
                    <InputNumber inputId="client-attempts" v-model="state.modifiedClient.webhookMaxAttempts" :min="1"
                        :max="10" showButtons />
                    <small class="data-detail-hint">
                        Envios automáticos ao callback antes de liberar o reenvio manual no painel.
                    </small>
                </div>

                <div class="form-field">
                    <label for="client-fallback">Fallback</label>
                    <Dropdown inputId="client-fallback" v-model="state.modifiedClient.fallbackEnabled" optionLabel="name"
                        optionValue="value" :options="fallbackOptions" />
                </div>

                <button type="submit" class="hidden"></button>
            </form>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="state.detailsModal = false" />
                <Button :loading="state.loading.save" :disabled="!pendingChanges.length" type="button"
                    label="Salvar alterações" icon="pi pi-check" @click="saveChanges" />
            </template>

        </Dialog>

        <Dialog class="data-dialog" v-model:visible="state.updateTokenModal" :style="{ width: '34rem' }" modal
            header="Gerar novo token" @update:visible="handleClose">

            <div class="data-form-grid data-form-single">
                <Message severity="warn" sticky :closable="false">
                    Gerar um novo token invalida o atual e ele só pode ser visto uma vez. Guarde-o em um lugar seguro
                    antes de fechar esta janela.
                </Message>

                <div v-if="state.clientToken" class="token-box">
                    <code>{{ state.clientToken }}</code>
                    <Button class="p-button-text p-button-sm" type="button" icon="pi pi-copy"
                        v-tooltip.left="{ value: 'Copiar token' }" @click="copyToken(state.clientToken)" />
                </div>
            </div>

            <template #footer>
                <Button v-if="state.clientToken" type="button" label="Já guardei o token" icon="pi pi-check"
                    @click="state.updateTokenModal = false" />
                <template v-else>
                    <Button class="p-button-text" type="button" label="Cancelar" @click="state.updateTokenModal = false" />
                    <Button :loading="state.loading.token" type="button" label="Gerar e visualizar token"
                        icon="pi pi-eye" @click="tryToUpdateToken" />
                </template>
            </template>

        </Dialog>

        <ConfirmDeleteDialog v-model:visible="state.deleteModal" header="Excluir cliente"
            :subjectId="state.clientToDelete.id ? formatId(state.clientToDelete.id) : null"
            :subjectName="state.clientToDelete.name" :loading="state.loading.delete"
            @confirm="tryToDeleteClient">
            Você tem certeza que deseja excluir esse cliente? O token dele deixa de ser aceito na hora e
            todas as chamadas dele à API passam a ser recusadas.
        </ConfirmDeleteDialog>
    </div>
</template>

<script>

import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Message from 'primevue/message'
import SplitButton from 'primevue/splitbutton'
import PageHeader from '../../components/PageHeader.vue'
import TableResults from '../../components/TableResults.vue'
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog.vue'
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import services from '../../services'
import useNotificationToast from '../../composables/useNotificationToast'
import useVendors from '../../composables/useVendors'
import { useStore } from 'vuex'

export default {
    components: { Button, DataTable, Column, Dialog, InputText, InputNumber, Message, Dropdown, SplitButton, PageHeader, TableResults, ConfirmDeleteDialog },

    setup() {
        const state = reactive({

            clients: [],
            client: {},
            modifiedClient: {},
            clientToDelete: {},
            clientToken: null,

            search: '',

            newClient: {
                name: null,
                callbackUrl: null,
                webhookMaxAttempts: null,
            },
            newClientToken: null,

            loading: {
                save: false,
                clients: false,
                token: false,
                create: false,
                delete: false
            },

            currentPage: 1,
            perPage: 20,
            totalRecords: 0,

            createModal: false,
            newClientTokenModal: false,

            detailsModal: false,

            updateTokenModal: false,

            deleteModal: false,

        })

        // mesmo padrão da coluna webhook_max_attempts no backend
        const DEFAULT_WEBHOOK_ATTEMPTS = 3

        // o Dropdown guarda booleano para comparar com o valor original da API
        const fallbackOptions = [
            { name: 'Ativado', value: true },
            { name: 'Desativado', value: false },
        ]

        const store = useStore()
        const { success, error } = useNotificationToast()
        const router = useRouter()

        loadClients()

        function loadClients() {
            state.loading.clients = true

            const params = {
                userFilter: state.userFilter,
                page: state.currentPage,
                perPage: state.perPage,
            }

            const token = store.getters.getToken

            services.clients.getAll({ params, token })
                .then((response) => {
                    state.clients = response.data.clients
                    state.currentPage = response.data.meta.current_page
                    state.totalRecords = response.data.meta.total
                })
                .catch(() => {
                    error("Não foi possível carregar os clientes.")
                })
                .finally(() => { state.loading.clients = false })

        }

        function onPage(event) {
            state.currentPage = event.page + 1
            state.perPage = event.rows
            loadClients()
        }

        const { stateVendors, loadVendors } = useVendors()

        loadVendors()

        const filteredClients = computed(() => {
            const term = state.search.trim().toLowerCase()

            if (!term) return state.clients

            return state.clients.filter((client) => {
                const vendorNames = (client.vendors || []).map((vendor) => vendor.vendorName).join(' ')
                return [client.name, client.callbackUrl, vendorNames, String(client.id)]
                    .some((value) => (value || '').toLowerCase().includes(term))
            })
        })

        const summaryText = computed(() => {
            const total = state.totalRecords
            const parts = [`${total} ${total === 1 ? 'cliente cadastrado' : 'clientes cadastrados'}`]

            // a contagem de fallback só é confiável quando todos os clientes estão na página atual
            if (state.clients.length && state.clients.length >= total) {
                const withFallback = state.clients.filter((client) => client.fallbackEnabled).length
                parts.push(`${withFallback} com fallback ativo`)
            }

            return parts.join(' · ')
        })

        function formatId(id) {
            return String(id).padStart(2, '0')
        }

        function changeStatusBoleanToText(active) {
            return active ? 'Ativado' : 'Desativado'
        }

        function openCreateModal() {
            clearNewClient()
            state.createModal = true
        }

        function handleCreateModalClose() {
            clearNewClient()
        }

        function clearNewClient() {
            state.newClient.name = null
            state.newClient.callbackUrl = null
            state.newClient.webhookMaxAttempts = DEFAULT_WEBHOOK_ATTEMPTS
        }

        function tryToRegisterClient() {

            if (!state.newClient.name) {
                return error('Verifique todos os campos!')
            }

            state.loading.create = true

            const token = store.getters.getToken

            services.clients.register({
                name: state.newClient.name,
                callbackUrl: state.newClient.callbackUrl,
                webhookMaxAttempts: state.newClient.webhookMaxAttempts,
                token
            }).then(({ data }) => {
                success('Cliente registrado com sucesso!')
                state.createModal = false
                clearNewClient()
                state.newClientToken = data.token
                state.newClientTokenModal = true
                loadClients()
            }).catch(() => {
                error('Não foi possível registrar o cliente.')
            }).finally(() => {
                state.loading.create = false
            })

        }

        function handleNewClientTokenClose() {
            state.newClientToken = null
        }

        function actionMenuItems(client) {
            return [
                {
                    label: 'Configurações do Cliente',
                    icon: 'pi pi-cog',
                    command: () => goToClientConfigurations(client.id)
                },
                {
                    label: 'Gerenciar Jogadores',
                    icon: 'pi pi-users',
                    command: () => goToClientPlayers(client.id)
                },
                {
                    label: 'Gerar novo Token',
                    icon: 'pi pi-lock-open',
                    class: 'text-red-400',
                    command: () => openUpdateTokenModal(client)
                },
                {
                    label: 'Excluir Cliente',
                    icon: 'pi pi-trash',
                    class: 'text-red-400',
                    command: () => openDeleteModal(client)
                },
            ]
        }

        function openDetailsModal(client) {

            state.client = client

            state.modifiedClient = { ...client }
            // a API devolve 1/0; o Dropdown de fallback trabalha com booleano
            state.modifiedClient.fallbackEnabled = !!client.fallbackEnabled

            state.detailsModal = true
        }

        function openUpdateTokenModal(client) {

            state.client = client

            state.updateTokenModal = true

        }

        function openDeleteModal(client) {
            state.clientToDelete = client
            state.deleteModal = true
        }

        function tryToDeleteClient() {

            state.loading.delete = true

            const clientId = state.clientToDelete.id
            const token = store.getters.getToken

            services.clients.delete({ clientId, token }).then(() => {
                success('Cliente excluído com sucesso')
                state.deleteModal = false
                state.clientToDelete = {}
                loadClients()
            }).catch((e) => {
                const message = e.response?.data?.message
                if (!message) return error('Não foi possível excluir o cliente.')
                error(message.charAt(0).toUpperCase() + message.slice(1))
            }).finally(() => {
                state.loading.delete = false
            })

        }

        function tryToUpdateToken() {

            state.loading.token = true
            const clientId = state.client.id
            const token = store.getters.getToken

            services.clients.updateToken({ clientId, token }).then(({ data }) => {
                success('Token de cliente atualizado com sucesso')
                state.clientToken = data.token
            }).catch(() => {
                error("Não foi possível atualizar o token.")
            }).finally(() => {
                state.loading.token = false
            })

        }

        async function copyToken(value) {

            try {
                await navigator.clipboard.writeText(value)
                success('Token copiado para a área de transferência')
            } catch {
                error('Não foi possível copiar. Selecione o token e copie manualmente.')
            }

        }

        function goToClientConfigurations(id) {
            router.push({
                name: 'client-configurations', params: { id }
            })
        }

        function goToClientPlayers(id) {
            router.push({
                name: 'players', params: { id }
            })
        }


        function changedItems() {
            const changed = []

            for (const key in state.client) {

                const original = state.client[key]
                const modified = state.modifiedClient[key]

                if (key === 'fallbackEnabled') {

                    if (!!original !== !!modified) {
                        changed.push({
                            original: !!original,
                            modified: !!modified,
                            field: key
                        })
                    }

                    continue
                }

                if (original != modified) {
                    changed.push({
                        original,
                        modified,
                        field: key
                    })
                }

            }

            return changed
        }

        const pendingChanges = computed(() => changedItems())

        function saveChanges() {

            if (!pendingChanges.value.length) return

            state.loading.save = true

            const modifiedClient = { ...state.modifiedClient }

            const token = store.getters.getToken

            services.clients.update({ modifiedClient, token }).then(() => {
                success('Cliente atualizado com sucesso')
                state.detailsModal = false
                loadClients()
            }).catch(() => {
                error("Não foi possível atualizar o cliente.")
            }).finally(() => {
                state.loading.save = false
            })

        }

        function handleClose() {
            state.clientToken = null
        }

        return {
            state,
            stateVendors,
            fallbackOptions,
            filteredClients,
            summaryText,
            pendingChanges,
            formatId,
            changeStatusBoleanToText,
            openCreateModal,
            handleCreateModalClose,
            tryToRegisterClient,
            handleNewClientTokenClose,
            actionMenuItems,
            openDetailsModal,
            openUpdateTokenModal,
            openDeleteModal,
            tryToDeleteClient,
            saveChanges,
            tryToUpdateToken,
            copyToken,
            handleClose,
            onPage
        }

    }
}
</script>
